import React, { useState, useEffect } from 'react';
import { EnhancedArtifact } from '../../../types/artifacts';
import { ArtifactService } from '../../../services/api/artifactService';
import { ArtifactTypeService } from '../../../services/api/artifactTypeService';
import { ArtifactTypeSelector } from './ArtifactTypeSelector';
import { ArtifactSelector } from './ArtifactSelector';

export interface ArtifactDetailsFormProps {
  artifact?: EnhancedArtifact;
  onSave: (updates: any) => void;
  onCancel?: () => void;
  disabled?: boolean;
  style?: React.CSSProperties;
}

export const ArtifactDetailsForm: React.FC<ArtifactDetailsFormProps> = ({
  artifact,
  onSave,
  onCancel,
  disabled = false,
  style = {}
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    artifact_type_id: '',
    parent_ref: ''
  });
  const [parentArtifact, setParentArtifact] = useState<EnhancedArtifact | null>(null);
  const [artifactTypeName, setArtifactTypeName] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (artifact) {
      setFormData({
        name: artifact.name || '',
        description: artifact.description || '',
        artifact_type_id: artifact.artifact_type_id || '',
        parent_ref: artifact.parent_ref || ''
      });
      
      // Load parent artifact if parent_ref exists
      if (artifact.parent_ref) {
        loadParentArtifact(artifact.parent_ref);
      }
      
      // Load artifact type name
      if (artifact.artifact_type_id) {
        loadArtifactTypeName(artifact.artifact_type_id);
      }
    }
  }, [artifact]);

  const loadParentArtifact = async (parentRef: string) => {
    try {
      const parent = await ArtifactService.resolveParentArtifact(parentRef);
      setParentArtifact(parent);
    } catch (error) {
      console.error('Failed to load parent artifact:', error);
    }
  };

  const loadArtifactTypeName = async (typeId: string) => {
    try {
      const typeName = await ArtifactTypeService.getArtifactTypeName(typeId);
      setArtifactTypeName(typeName);
    } catch (error) {
      console.error('Failed to load artifact type name:', error);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Load related data when fields change
    if (field === 'parent_ref') {
      if (value) {
        loadParentArtifact(value);
      } else {
        setParentArtifact(null);
      }
    } else if (field === 'artifact_type_id') {
      if (value) {
        loadArtifactTypeName(value);
      } else {
        setArtifactTypeName('');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Failed to save artifact:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (artifact) {
      setFormData({
        name: artifact.name || '',
        description: artifact.description || '',
        artifact_type_id: artifact.artifact_type_id || '',
        parent_ref: artifact.parent_ref || ''
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} style={style}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Name Field */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '4px'
          }}>
            Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            disabled={disabled}
            required
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: disabled ? '#f9fafb' : 'white'
            }}
            placeholder="Enter artifact name"
          />
        </div>

        {/* Description Field */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '4px'
          }}>
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            disabled={disabled}
            rows={3}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: disabled ? '#f9fafb' : 'white',
              resize: 'vertical'
            }}
            placeholder="Enter artifact description"
          />
        </div>

        {/* Artifact Type Field */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '4px'
          }}>
            Artifact Type *
          </label>
          <ArtifactTypeSelector
            value={formData.artifact_type_id}
            onChange={(value) => handleFieldChange('artifact_type_id', value)}
            disabled={disabled}
            placeholder="Select artifact type"
          />
          {artifactTypeName && (
            <div style={{
              fontSize: '11px',
              color: '#6b7280',
              marginTop: '4px'
            }}>
              Selected: {artifactTypeName}
            </div>
          )}
        </div>

        {/* Parent Artifact Field */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '4px'
          }}>
            Parent Artifact
          </label>
          <ArtifactSelector
            value={formData.parent_ref}
            onChange={(value) => handleFieldChange('parent_ref', value)}
            disabled={disabled}
            excludeArtifactId={artifact?.id}
            placeholder="Select parent artifact (optional)"
            showDescription={true}
          />
          {parentArtifact && (
            <div style={{
              fontSize: '11px',
              color: '#6b7280',
              marginTop: '4px'
            }}>
              Selected: {parentArtifact.name}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '8px',
          justifyContent: 'flex-end',
          paddingTop: '16px',
          borderTop: '1px solid #e5e7eb'
        }}>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              style={{
                padding: '8px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: 'white',
                color: '#374151',
                fontSize: '14px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleReset}
            disabled={loading || disabled}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: 'white',
              color: '#374151',
              fontSize: '14px',
              cursor: loading || disabled ? 'not-allowed' : 'pointer',
              opacity: loading || disabled ? 0.6 : 1
            }}
          >
            Reset
          </button>
          <button
            type="submit"
            disabled={loading || disabled || !formData.name || !formData.artifact_type_id}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: loading || disabled || !formData.name || !formData.artifact_type_id ? '#9ca3af' : '#3b82f6',
              color: 'white',
              fontSize: '14px',
              cursor: loading || disabled || !formData.name || !formData.artifact_type_id ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.8 : 1
            }}
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </form>
  );
};
